import type { ApiClient } from '@kiao-voice/api-client'
import { createWebApiClient, getWebApiClient } from '../../lib/web-api-client'
import { mapBatchError } from '../batch-transcription/api-client'
import type {
  NotesTemplate,
  NotesTemplateListResponse,
  TranscriptNote,
  TranscriptNotesResponse,
  PublicNoteShare,
  PublicNoteShareResponse,
  OwnedPublicNoteShare,
} from './types'
import type { NotesServerExportFormat } from './notes-export-formats'
import type { WritableNotesTemplateScope } from '@kiao-voice/core'

/**
 * 會議記錄整理 — the two API surfaces this feature talks to: account-scoped
 * 整理範本, and the notes belonging to one transcript item.
 *
 * Errors go through the SAME `mapBatchError` the transcript client uses, so a
 * stale active context still satisfies `isRecoverableActiveContextError` and
 * the page's existing retry fires unchanged. A private near-copy here would
 * silently break that.
 */

interface RequestOptions {
  /** Test seam. */
  fetchFn?: typeof fetch
}

function client(opts?: RequestOptions): ApiClient {
  return opts?.fetchFn ? createWebApiClient(opts.fetchFn) : getWebApiClient()
}

async function request<T>(
  path: string,
  method: string,
  opts: RequestOptions,
  body?: unknown
): Promise<T> {
  try {
    return await client(opts).request<T>(`/api/v1${path}`, {
      method,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch (error) {
    throw mapBatchError(error, (status) => `Request failed (HTTP ${status}).`)
  }
}

// ---------------------------------------------------------------------------
// 整理範本 (account-scoped)
// ---------------------------------------------------------------------------

export async function fetchNotesTemplates(
  opts: RequestOptions = {}
): Promise<NotesTemplateListResponse> {
  return request<NotesTemplateListResponse>('/notes-templates', 'GET', opts)
}

export interface NotesTemplateInput {
  name: string
  description?: string
  markdown: string
  scope?: WritableNotesTemplateScope
  isDefault?: boolean
}

export async function createNotesTemplate(
  input: NotesTemplateInput,
  opts: RequestOptions = {}
): Promise<NotesTemplate> {
  const res = await request<{ template: NotesTemplate }>('/notes-templates', 'POST', opts, input)
  return res.template
}

export async function updateNotesTemplate(
  id: number,
  input: Partial<Omit<NotesTemplateInput, 'scope'>>,
  opts: RequestOptions = {}
): Promise<NotesTemplate> {
  const res = await request<{ template: NotesTemplate }>(
    `/notes-templates/${id}`,
    'PATCH',
    opts,
    input
  )
  return res.template
}

/** 複製成我的 / 複製 — one endpoint; only the button label differs. */
export async function duplicateNotesTemplate(
  id: number,
  scope: WritableNotesTemplateScope = 'personal',
  opts: RequestOptions = {}
): Promise<NotesTemplate> {
  const res = await request<{ template: NotesTemplate }>(
    `/notes-templates/${id}/duplicate`,
    'POST',
    opts,
    { scope }
  )
  return res.template
}

export async function deleteNotesTemplate(id: number, opts: RequestOptions = {}): Promise<void> {
  await request<{ deleted: true }>(`/notes-templates/${id}`, 'DELETE', opts)
}

// ---------------------------------------------------------------------------
// 會議記錄 (per transcript item)
// ---------------------------------------------------------------------------

export async function fetchTranscriptNotes(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<TranscriptNotesResponse> {
  return request<TranscriptNotesResponse>(
    `/audio-batches/${batchId}/items/${itemId}/notes`,
    'GET',
    opts
  )
}

/** Queue a run. 402 (`insufficient_allowance`) when the pool cannot cover it. */
export async function startTranscriptNote(
  batchId: number,
  itemId: number,
  templateId: number,
  opts: RequestOptions = {}
): Promise<TranscriptNote> {
  const res = await request<{ note: TranscriptNote }>(
    `/audio-batches/${batchId}/items/${itemId}/notes`,
    'POST',
    opts,
    { templateId }
  )
  return res.note
}

/**
 * Save in-place edits.
 *
 * The server re-renders the HTML through its own renderer before storing, so
 * the response can differ from what was sent (an unsupported tag is dropped).
 * Apply what comes back rather than assuming the local DOM won — otherwise the
 * next reload silently shows something else.
 */
export async function saveTranscriptNote(
  batchId: number,
  itemId: number,
  noteId: number,
  contentHtml: string,
  opts: RequestOptions = {}
): Promise<TranscriptNote> {
  const res = await request<{ note: TranscriptNote }>(
    `/audio-batches/${batchId}/items/${itemId}/notes/${noteId}`,
    'PATCH',
    opts,
    { contentHtml }
  )
  return res.note
}

/** 重新整理這一份 — same template, overwrites the current document. */
export async function regenerateTranscriptNote(
  batchId: number,
  itemId: number,
  noteId: number,
  opts: RequestOptions = {}
): Promise<TranscriptNote> {
  const res = await request<{ note: TranscriptNote }>(
    `/audio-batches/${batchId}/items/${itemId}/notes/${noteId}/regenerate`,
    'POST',
    opts
  )
  return res.note
}

/**
 * 下載 Word / PDF.
 *
 * Goes through `requestBlob` rather than `request` so the binary body is not
 * run through the JSON parser, and through `mapBatchError` like every other
 * call here so an active-context failure still triggers the page's retry.
 */
export async function exportTranscriptNote(
  batchId: number,
  itemId: number,
  noteId: number,
  format: NotesServerExportFormat,
  opts: RequestOptions = {}
): Promise<Blob> {
  const path = `/api/v1/audio-batches/${batchId}/items/${itemId}/notes/${noteId}/export?format=${format}`
  try {
    return await client(opts).requestBlob(path, { method: 'GET' })
  } catch (error) {
    throw mapBatchError(error, (status) => `Notes export failed (HTTP ${status}).`)
  }
}

export async function deleteTranscriptNote(
  batchId: number,
  itemId: number,
  noteId: number,
  opts: RequestOptions = {}
): Promise<void> {
  await request<{ deleted: true }>(
    `/audio-batches/${batchId}/items/${itemId}/notes/${noteId}`,
    'DELETE',
    opts
  )
}

// ---------------------------------------------------------------------------
// Public meeting-note snapshot (per transcript item)
// ---------------------------------------------------------------------------

function publicLinkPath(batchId: number, itemId: number): string {
  return `/audio-batches/${batchId}/items/${itemId}/public-link`
}

export async function fetchPublicNoteShare(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<PublicNoteShareResponse> {
  return request<PublicNoteShareResponse>(publicLinkPath(batchId, itemId), 'GET', opts)
}

export async function createPublicNoteShare(
  batchId: number,
  itemId: number,
  noteId: number,
  opts: RequestOptions = {}
): Promise<PublicNoteShare> {
  const response = await request<{ share: PublicNoteShare }>(
    publicLinkPath(batchId, itemId),
    'POST',
    opts,
    { noteId }
  )
  return response.share
}

export async function refreshPublicNoteShare(
  batchId: number,
  itemId: number,
  noteId?: number,
  opts: RequestOptions = {}
): Promise<PublicNoteShare> {
  const response = await request<{ share: PublicNoteShare }>(
    `${publicLinkPath(batchId, itemId)}/refresh`,
    'POST',
    opts,
    noteId === undefined ? {} : { noteId }
  )
  return response.share
}

export async function revokePublicNoteShare(
  batchId: number,
  itemId: number,
  opts: RequestOptions = {}
): Promise<void> {
  await request<{ revoked: true }>(publicLinkPath(batchId, itemId), 'DELETE', opts)
}

/** Creator-owned live links, including snapshots whose source item expired. */
export async function listOwnedPublicNoteShares(
  opts: RequestOptions = {}
): Promise<OwnedPublicNoteShare[]> {
  const response = await request<{ shares: OwnedPublicNoteShare[] }>('/note-shares', 'GET', opts)
  return response.shares
}

/** Revoke without depending on a retained batch/item route. */
export async function revokeOwnedPublicNoteShare(
  token: string,
  opts: RequestOptions = {}
): Promise<void> {
  await request<{ revoked: true }>(`/note-shares/${encodeURIComponent(token)}`, 'DELETE', opts)
}
